import { Phone, Mail, Facebook, MessageCircle, MapPin } from 'lucide-react';
import { CONTACT, CONTACT_PAGE } from '../data/content';
import { Helmet } from 'react-helmet-async';

// Obfuscate email/phone so bots can't scrape plain text from source
function ObfuscatedText({ text, dataTestId }) {
  return (
    <span data-testid={dataTestId}>
      {text.split('').map((char, i) => (
        <span key={i} aria-hidden="true">{char}</span>
      ))}
      <span className="sr-only">{text}</span>
    </span>
  );
}

export default function ContactPage() {
  return (
    <div data-testid="contact-page" className="pt-20">
      <Helmet>
        <title>Kapcsolat | Mosolytorna Dunakeszi</title>
        <meta name="description" content="Kérj időpontot logopédiai vizsgálatra vagy fejlesztésre Dunakeszin! Elérhetőségek, telefonszám és e-mail cím." />
      </Helmet>
      <section className="py-12 md:py-20 bg-gradient-to-b from-cyan-50 via-purple-50 to-pink-50" aria-labelledby="contact-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">

            {/* Header */}
            <div className="text-center mb-8 md:mb-12">
              <h1
                id="contact-heading"
                className="text-3xl sm:text-4xl md:text-5xl font-light mb-4 md:mb-6 text-gray-800"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="contact-title"
              >
                {CONTACT_PAGE.title}
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 px-2">
                {CONTACT_PAGE.subtitle}
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 md:p-12">

              {/* Contact Info */}
              <section data-testid="contact-info-section" aria-labelledby="contact-info-heading">
                <h2
                  id="contact-info-heading"
                  className="text-xl md:text-2xl font-light text-gray-800 mb-5 md:mb-6"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {CONTACT_PAGE.captchaTitle}
                </h2>

                {/* Phone + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                  <a
                    href={`tel:${CONTACT.phoneTel}`}
                    data-testid="contact-phone-link"
                    className="flex items-center gap-3 md:gap-4 p-4 md:p-6 bg-gradient-to-br from-pink-100 to-orange-100 rounded-2xl hover:from-pink-200 hover:to-orange-200 transition-all duration-200 group shadow-md hover:shadow-lg no-underline"
                    aria-label={`Telefon: ${CONTACT.phoneDisplay}`}
                  >
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-pink-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200 shadow-md">
                      <Phone className="w-5 h-5 md:w-7 md:h-7 text-white" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs md:text-sm text-gray-500 mb-0.5">Telefon</p>
                      <p className="text-base md:text-xl font-semibold text-gray-800">
                        <ObfuscatedText text={CONTACT.phoneDisplay} dataTestId="contact-phone-number" />
                      </p>
                    </div>
                  </a>

                  <a
                    href={`mailto:${CONTACT.email}`}
                    data-testid="contact-email-link"
                    className="flex items-center gap-3 md:gap-4 p-4 md:p-6 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl hover:from-cyan-200 hover:to-blue-200 transition-all duration-200 group shadow-md hover:shadow-lg min-w-0 no-underline"
                    aria-label={`E-mail: ${CONTACT.email}`}
                  >
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200 shadow-md">
                      <Mail className="w-5 h-5 md:w-7 md:h-7 text-white" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm text-gray-500 mb-0.5">E-mail</p>
                      <p className="text-sm md:text-lg font-semibold text-gray-800 truncate">
                        <ObfuscatedText text={CONTACT.email} dataTestId="contact-email-address" />
                      </p>
                    </div>
                  </a>
                </div>

                {/* Facebook + WhatsApp */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mt-4 md:mt-6">
                  
                  {/* Facebook Kártya */}
                  <a
                    href={CONTACT.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-facebook-btn"
                    className="relative overflow-hidden flex items-center gap-4 p-4 md:p-6 bg-white border-2 border-blue-50 rounded-2xl hover:border-blue-200 hover:shadow-xl hover:shadow-blue-100 transition-all duration-300 group no-underline"
                    aria-label="Facebook profil megnyitása (új ablakban)"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Avatar Profilképpel és Facebook logóval */}
                    <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-full flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform duration-300">
                      <img src="/profile.png" alt="Mosolytorna profil" className="w-full h-full object-cover rounded-full border-2 border-white shadow-sm" />
                      <div className="absolute -bottom-1 -right-1 bg-blue-600 w-7 h-7 rounded-full border-[2.5px] border-white flex items-center justify-center shadow-md">
                        <Facebook className="w-3.5 h-3.5 text-white fill-current" aria-hidden="true" />
                      </div>
                    </div>

                    <div className="z-10">
                      <p className="text-xs md:text-sm font-bold text-blue-600 mb-0.5 tracking-wide uppercase">Közösség</p>
                      <p className="text-base md:text-xl font-bold text-gray-800 group-hover:text-blue-700 transition-colors">Facebook profil</p>
                    </div>
                  </a>

                  {/* WhatsApp Kártya */}
                  <a
                    href={CONTACT.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-btn"
                    className="relative overflow-hidden flex items-center gap-4 p-4 md:p-6 bg-white border-2 border-emerald-50 rounded-2xl hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-100 transition-all duration-300 group no-underline"
                    aria-label="WhatsApp üzenet küldése (új ablakban)"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Avatar Profilképpel és WhatsApp/Online jelzővel */}
                    <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-full flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform duration-300">
                      <img src="/profile.png" alt="Mosolytorna profil" className="w-full h-full object-cover rounded-full border-2 border-white shadow-sm" />
                      <div className="absolute -bottom-1 -right-1 bg-[#25D366] w-7 h-7 rounded-full border-[2.5px] border-white flex items-center justify-center shadow-md">
                        <MessageCircle className="w-3.5 h-3.5 text-white" aria-hidden="true" />
                      </div>
                      {/* Pulzáló zöld "Online" pont */}
                      <span className="absolute top-0 right-0 flex h-3.5 w-3.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-green-500 border-2 border-white"></span>
                      </span>
                    </div>

                    <div className="z-10">
                      <p className="text-xs md:text-sm font-bold text-emerald-600 mb-0.5 tracking-wide uppercase">Azonnali üzenet</p>
                      <p className="text-base md:text-xl font-bold text-gray-800 group-hover:text-emerald-700 transition-colors">Írj WhatsApp-on</p>
                    </div>
                  </a>
                </div>

                {/* === ÚJ TÉRKÉP SZEKCIÓ === */}
                <div className="mt-10 md:mt-14 pt-8 border-t border-gray-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-500">
                      <MapPin className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <h3 className="text-lg md:text-xl font-medium text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Itt találsz meg: Dunakeszi
                    </h3>
                  </div>
                  
                  <div className="w-full h-64 sm:h-80 md:h-96 rounded-2xl overflow-hidden shadow-lg border-2 border-orange-100 relative group">
                    <iframe
                      src="https://maps.google.com/maps?q=Mosolytorna%20Logop%C3%A9dia%20Dunakeszi&t=&z=15&ie=UTF8&iwloc=&output=embed"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Mosolytorna Logopédia Térkép"
                      className="absolute inset-0 w-full h-full"
                    ></iframe>
                  </div>
                </div>
                {/* === TÉRKÉP SZEKCIÓ VÉGE === */}

              </section>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
